import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StringNode, widget, WidgetPlugin } from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';
import { CreatableSingleSelect } from '../../../components/selects/CreatableSingleSelect';
import { CreatableSelectWidgetOptions } from '../selectWidgetOptions';
import {
  isNullableString,
  resolvePlaceholder,
  useErrorTransformation,
} from '../widgetUtils';

export type CreatableSelectOptions = CreatableSelectWidgetOptions;

function getSuggestionValues(
  schema: StringNode<CreatableSelectOptions>['schema']
): string[] {
  const schemaOptions = schema.options as CreatableSelectOptions | undefined;
  const fromSuggestions =
    schemaOptions?.suggestions?.filter(
      (value): value is string => value != null
    ) ?? [];
  if (fromSuggestions.length > 0) {
    return fromSuggestions;
  }

  // Backward compatibility while schemas still use schema.enum for suggestions.
  return (schema.enum?.filter((value) => value != null) || []) as string[];
}

export const CreatableSelectWidget = widget<
  StringNode<CreatableSelectOptions>,
  string | null
>(({ node, options, setValue }) => {
  const { t } = useTranslation();
  const hasError = node.errors.length > 0;
  const titles = useMemo(
    () => (node.schema.options?.enum as string[] | undefined) ?? [],
    [node.schema.options?.enum]
  );
  const errors = useErrorTransformation(node.errors);
  const suggestionValues = useMemo(
    () => getSuggestionValues(node.schema),
    [node.schema]
  );
  const selectData = useMemo(
    () =>
      suggestionValues.map((value, index) => ({
        label: titles[index] ?? value,
        value,
      })),
    [suggestionValues, titles]
  );

  return (
    <FormField
      widgetType="select"
      node={node}
      errors={errors}
      options={options}
      tableLayout={options.tableLayout}
    >
      {options.readOnly ? (
        <ReadOnlyInput
          description={options.description}
          label={options.title}
          value={node.value}
          placeholder={options.placeholder}
          tableLayout={options.tableLayout}
        />
      ) : (
        <CreatableSingleSelect
          data={selectData}
          description={options.description}
          label={options.title}
          placeholder={resolvePlaceholder(
            options.placeholder,
            t('app.forms.placeholders.creatableSelect')
          )}
          error={options.showErrors !== false && hasError}
          clearable={options.clearable === true}
          required={options.required}
          disabled={options.disabled}
          loading={options.loading}
          readOnly={options.readOnly}
          value={node.value ?? null}
          tableLayout={options.tableLayout}
          onChange={(value) => setValue(value)}
        />
      )}
    </FormField>
  );
});

export const CreatableSelectWidgetPlugin: WidgetPlugin = {
  id: 'creatable-select-widget',
  use: (node) =>
    isNullableString(node.schema.type) &&
    node.schema.format === 'creatable-select',
  Widget: CreatableSelectWidget,
};
